import { create } from "zustand";
import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  useForm,
} from "@/components/ui/form";
import z from "zod";
import { useTranslation } from "react-i18next";
import { getSessionId } from "@/lib/session-cache";
import { checkDeliveryArea } from "@/lib/delivery-areas";
import { useAlert } from "./use-alert";
import { useWatch } from "react-hook-form";

interface ZipCodeDialogOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  placeholder?: string;
  defaultValue?: string;
}

interface ZipCodeDialogState {
  isOpen: boolean;
  options: ZipCodeDialogOptions | null;
  resolve:
    | ((
        value: {
          zipcode: string;
          streetName: string;
          streetNumber: string;
        } | null
      ) => void)
    | null;
}

const useZipCodeDialogStore = create<ZipCodeDialogState>(() => ({
  isOpen: false,
  options: null,
  resolve: null,
}));

export function useZipCodeDialog() {
  const showZipCodeDialog = (
    options: ZipCodeDialogOptions
  ): Promise<{
    zipcode: string;
    streetName: string;
    streetNumber: string;
  } | null> => {
    return new Promise((resolve) => {
      useZipCodeDialogStore.setState({
        isOpen: true,
        options,
        resolve,
      });
    });
  };

  return { showZipCodeDialog };
}

export function ZipCodeDialogProvider() {
  const { isOpen, options, resolve } = useZipCodeDialogStore();

  //hooks
  const { showAlert } = useAlert();

  const { t } = useTranslation("translation");

  const form = useForm({
    schema: z.object({
      zipcode: z.string().min(1, "Zip code is required"),
      streetNumber: z.string().min(1, "Street number is required"),
      streetName: z.string().min(1, "Street name is required"),
    }),
    defaultValues: {
      zipcode: "",
      streetNumber: "",
      streetName: "",
    },
  });

  const handleClose = (
    value: {
      zipcode: string;
      streetName: string;
      streetNumber: string;
    } | null
  ) => {
    useZipCodeDialogStore.setState({ isOpen: false });
    if (resolve) {
      resolve(value);
    }
    // Reset state after a delay to allow for animations
    setTimeout(() => {
      useZipCodeDialogStore.setState({ options: null, resolve: null });
    }, 200);
  };

  const handleFetchAddress = async (
    postalCode?: string,
    streetNumber?: string
  ) => {
    if (!postalCode || !streetNumber) {
      return;
    }
    const sessionId = getSessionId(postalCode, streetNumber);
    form.setValue("streetName", "");
    try {
      const result = await checkDeliveryArea({
        data: {
          streetNumber,
          zipCode: postalCode,
          sessionId,
        },
      });

      if (result.address) {
        form.setValue("streetName", result.address.street);
      } else {
        if (result.code === "address_not_in_delivery_area") {
          showAlert({
            title: t("Checkout.Errors.address_not_in_delivery_area.title"),
            description: t(
              "Checkout.Errors.address_not_in_delivery_area.description"
            ),
            variant: "destructive",
          });
        } else if (result.code === "delivery_area_unavailable_today") {
          showAlert({
            title: t("Checkout.Errors.delivery_area_unavailable_today.title"),
            description: t(
              "Checkout.Errors.delivery_area_unavailable_today.description"
            ),
            variant: "destructive",
          });
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = form.handleSubmit((data) => {
    handleClose(data);
  });

  const streetName = useWatch({
    control: form.control,
    name: "streetName",
  });

  if (!options) return null;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleClose(null);
        }
      }}
    >
      <DialogContent>
        <Form {...form}>
          <form onSubmit={handleSubmit}>
            <DialogHeader>
              <DialogTitle>{options.title}</DialogTitle>
              {options.description && (
                <DialogDescription>{options.description}</DialogDescription>
              )}
            </DialogHeader>

            <div className="py-4 flex flex-col gap-2">
              <FormField
                control={form.control}
                name="zipcode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("AddressConfirmation.zipcode")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onBlur={() => {
                          handleFetchAddress(
                            field.value,
                            form.getValues("streetNumber")
                          );
                        }}
                        type="text"
                        placeholder={options.placeholder || "Enter zip code"}
                        defaultValue={options.defaultValue}
                        autoFocus
                        className="mt-2"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="streetNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t("AddressConfirmation.streetNumber")}
                    </FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        onBlur={() => {
                          handleFetchAddress(
                            form.getValues("zipcode"),
                            field.value
                          );
                        }}
                        type="text"
                        defaultValue={options.defaultValue}
                        className="mt-2"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="streetName"
                disabled
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("AddressConfirmation.streetName")}</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="text"
                        defaultValue={options.defaultValue}
                        autoFocus
                        className="mt-2"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => handleClose(null)}
              >
                {options.cancelText || "Cancel"}
              </Button>
              <Button type="submit" disabled={!streetName}>
                {options.confirmText || "Confirm"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
